/**
 * Service Worker Killer
 * Unregisters all service workers and optionally clears caches
 * Critical for Lovable previews to avoid React instance conflicts
 */
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';

const SW_TIMEOUT_MS = 3000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>((resolve) => {
      setTimeout(() => {
        if (import.meta.env.DEV) console.warn(`[SW Killer] ${label} timed out after ${ms}ms`);
        resolve(undefined);
      }, ms);
    }),
  ]);
}

export async function killAllServiceWorkers(clearCaches = true): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    if (import.meta.env.DEV) console.log('[SW Killer] Service workers not supported');
    return;
  }

  try {
    const registrations = await withTimeout(
      navigator.serviceWorker.getRegistrations(),
      SW_TIMEOUT_MS,
      'getRegistrations'
    );

    if (registrations && registrations.length > 0) {
      if (import.meta.env.DEV) console.log(`[SW Killer] Unregistering ${registrations.length} service worker(s)...`);
      await Promise.all(registrations.map(reg => 
        withTimeout(reg.unregister(), SW_TIMEOUT_MS, 'unregister')
      ));
      if (import.meta.env.DEV) console.log('[SW Killer] All service workers unregistered');
    }

    if (clearCaches && 'caches' in window) {
      const cacheNames = await withTimeout(caches.keys(), SW_TIMEOUT_MS, 'caches.keys');
      if (cacheNames && cacheNames.length > 0) {
        if (import.meta.env.DEV) console.log(`[SW Killer] Clearing ${cacheNames.length} cache(s)...`);
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        if (import.meta.env.DEV) console.log('[SW Killer] All caches cleared');
      }
    }

  } catch (error) {
    if (import.meta.env.DEV) logger.error('[SW Killer] Error during cleanup:', toError(error).message);
  }
}
